import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';
import {
  buildPushAudiencePayload,
  buildPushGradeOptions,
  buildPushGradeSplitOptions,
  formatPushTargetSummary,
  togglePushTarget,
} from '../lib/pushNotificationTargets';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Everyone' },
  { value: 'parents', label: 'Parents' },
  { value: 'staff', label: 'Staff' },
];

const EMPTY_COUNTS = {
  total: 0,
  parents: 0,
  staff: 0,
  total_registered: 0,
};

const EMPTY_TOTALS = {
  campaign_count: 0,
  target_count: 0,
  sent_count: 0,
  failed_count: 0,
  opened_count: 0,
  scheduled_count: 0,
};

const SCHEDULE_OPTIONS = [
  { value: 'immediate', label: 'Now' },
  { value: 'specific_time', label: 'Specific Time' },
  { value: 'dismissal_relative', label: 'Dismissal Time' },
];

function getTodayInputValue() {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatDate(value) {
  if (!value) return 'Not sent';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not sent';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatOpenRate(opened, sent) {
  const sentCount = Number(sent || 0);
  if (!sentCount) return '0%';
  return `${Math.round((Number(opened || 0) / sentCount) * 100)}%`;
}

function getAudienceLabel(value) {
  return AUDIENCE_OPTIONS.find((option) => option.value === value)?.label || 'Everyone';
}

function getStatusLabel(status) {
  if (status === 'scheduled') return 'Scheduled';
  if (status === 'sending') return 'Sending';
  if (status === 'failed') return 'Failed';
  if (status === 'queued') return 'Queued';
  if (status === 'canceled') return 'Canceled';
  return 'Sent';
}

function canCancelCampaign(campaign) {
  return ['scheduled', 'queued'].includes(campaign.status);
}

function canDeleteCampaign(campaign) {
  return campaign.status !== 'sending';
}

function formatScheduleSummary(campaign) {
  if (campaign.status === 'canceled') {
    return campaign.canceled_at ? `Canceled ${formatDate(campaign.canceled_at)}` : 'Canceled';
  }

  if (campaign.schedule_type === 'specific_time') {
    return campaign.scheduled_for ? `Scheduled for ${formatDate(campaign.scheduled_for)}` : 'Scheduled';
  }

  if (campaign.schedule_type === 'dismissal_relative') {
    const minutes = Number(campaign.relative_minutes || 0);
    const unit = minutes === 1 ? 'minute' : 'minutes';
    const direction = campaign.relative_direction === 'after' ? 'after' : 'before';
    return `${minutes} ${unit} ${direction} dismissal`;
  }

  return 'Immediate send';
}

function formatCampaignTime(campaign) {
  if (campaign.status === 'scheduled') return formatDate(campaign.scheduled_for);
  if (campaign.status === 'canceled') return formatDate(campaign.scheduled_for || campaign.created_at);
  return formatDate(campaign.sent_at || campaign.created_at);
}

export default function PushNotificationsPage({ token, schoolId }) {
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [gradeNames, setGradeNames] = useState([]);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [targetedParentCount, setTargetedParentCount] = useState(null);
  const [targetCountLoading, setTargetCountLoading] = useState(false);
  const [targetCountError, setTargetCountError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sendNotice, setSendNotice] = useState('');
  const [busyCampaignId, setBusyCampaignId] = useState(null);
  const [form, setForm] = useState({
    audience: 'all',
    targetGrades: [],
    targetGradeSplitIds: [],
    title: '',
    body: '',
    scheduleType: 'immediate',
    scheduleDate: getTodayInputValue(),
    scheduleTime: '15:00',
    relativeMinutes: '15',
    relativeDirection: 'before',
  });

  const load = useCallback(async () => {
    if (!schoolId || !token) return;

    setError('');
    setLoading(true);

    try {
      const [summaryRes, campaignsRes, gradeNamesRes, gradeSplitsRes] = await Promise.all([
        apiFetch(`/push-notifications/school/${schoolId}/summary`, {}, token),
        apiFetch(`/push-notifications/school/${schoolId}/campaigns?limit=50`, {}, token),
        apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-splits?school_id=${schoolId}&is_active=true`, {}, token).catch(() => ({ data: [] })),
      ]);

      setSummary(summaryRes.data || null);
      setCampaigns(campaignsRes.data || summaryRes.data?.recent_campaigns || []);
      setGradeNames(gradeNamesRes.data || []);
      setGradeSplits(gradeSplitsRes.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load push notification activity.');
      setSummary(null);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const audienceCounts = summary?.audience_counts || EMPTY_COUNTS;
  const totals = summary?.totals || EMPTY_TOTALS;
  const gradeOptions = useMemo(() => buildPushGradeOptions(gradeNames), [gradeNames]);
  const gradeSplitOptions = useMemo(
    () => buildPushGradeSplitOptions(gradeSplits, gradeNames),
    [gradeNames, gradeSplits]
  );
  const hasParentGroupTargets = form.audience === 'parents'
    && Boolean(form.targetGrades.length || form.targetGradeSplitIds.length);
  const allParentGroupsSelected = gradeOptions.length > 0
    && gradeOptions.every((grade) => form.targetGrades.includes(grade.gradeCode))
    && gradeSplitOptions.every((split) => form.targetGradeSplitIds.includes(split.splitId));

  useEffect(() => {
    if (!schoolId || !token || !hasParentGroupTargets) {
      setTargetedParentCount(null);
      setTargetCountLoading(false);
      setTargetCountError('');
      return undefined;
    }

    let cancelled = false;
    setTargetedParentCount(null);
    setTargetCountLoading(true);
    setTargetCountError('');

    apiFetch(`/push-notifications/school/${schoolId}/audience-count`, {
      method: 'POST',
      body: JSON.stringify({
        audience: 'parents',
        target_grades: form.targetGrades,
        target_grade_split_ids: form.targetGradeSplitIds,
      }),
    }, token)
      .then((res) => {
        if (!cancelled) setTargetedParentCount(Number(res.data?.count || 0));
      })
      .catch((err) => {
        if (!cancelled) setTargetCountError(err.message || 'Unable to count reachable parent devices.');
      })
      .finally(() => {
        if (!cancelled) setTargetCountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [form.targetGradeSplitIds, form.targetGrades, hasParentGroupTargets, schoolId, token]);

  const selectedAudienceCount = useMemo(() => {
    if (form.audience === 'parents') {
      return hasParentGroupTargets ? targetedParentCount : Number(audienceCounts.parents || 0);
    }
    if (form.audience === 'staff') return Number(audienceCounts.staff || 0);
    return Number(audienceCounts.total || 0);
  }, [
    audienceCounts.parents,
    audienceCounts.staff,
    audienceCounts.total,
    form.audience,
    hasParentGroupTargets,
    targetedParentCount,
  ]);

  const scheduleReady = form.scheduleType === 'immediate'
    || (form.scheduleType === 'specific_time' && form.scheduleDate && form.scheduleTime)
    || (
      form.scheduleType === 'dismissal_relative'
      && form.scheduleDate
      && String(form.relativeMinutes).trim() !== ''
    );
  const canSend = form.title.trim() && form.body.trim() && scheduleReady && !sending;
  const overallOpenRate = formatOpenRate(totals.opened_count, totals.sent_count);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setSendNotice('');
  };

  const handleTargetToggle = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: togglePushTarget(current[field], value),
    }));
    setSendNotice('');
  };

  const handleSelectAllTargets = () => {
    setForm((current) => ({
      ...current,
      targetGrades: gradeOptions.map((grade) => grade.gradeCode),
      targetGradeSplitIds: gradeSplitOptions.map((split) => split.splitId),
    }));
    setSendNotice('');
  };

  const handleClearTargets = () => {
    setForm((current) => ({
      ...current,
      targetGrades: [],
      targetGradeSplitIds: [],
    }));
    setSendNotice('');
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!canSend) return;

    setSending(true);
    setError('');
    setSendNotice('');

    try {
      const res = await apiFetch(`/push-notifications/school/${schoolId}/send`, {
        method: 'POST',
        body: JSON.stringify({
          ...buildPushAudiencePayload(form),
          title: form.title.trim(),
          body: form.body.trim(),
          schedule_type: form.scheduleType,
          schedule_date: form.scheduleType === 'immediate' ? null : form.scheduleDate,
          schedule_time: form.scheduleType === 'specific_time' ? `${form.scheduleTime}:00` : null,
          relative_minutes: form.scheduleType === 'dismissal_relative' ? Number(form.relativeMinutes) : null,
          relative_direction: form.scheduleType === 'dismissal_relative' ? form.relativeDirection : null,
        }),
      }, token);

      const sentCount = Number(res.data?.sent_count || 0);
      const failedCount = Number(res.data?.failed_count || 0);
      setSendNotice(
        res.data?.status === 'scheduled'
          ? `Notification scheduled for ${formatDate(res.data.scheduled_for)}.`
          : (res.message || `Notification sent to ${formatNumber(sentCount)} device${sentCount === 1 ? '' : 's'}${failedCount ? `, ${formatNumber(failedCount)} failed` : ''}.`)
      );
      setForm((current) => ({ ...current, title: '', body: '' }));
      await load();
    } catch (err) {
      setError(err.message || 'Unable to send push notification.');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (campaign) => {
    const confirmed = window.confirm(
      `Stop the scheduled notification "${campaign.title}"? It will not be delivered.`
    );
    if (!confirmed) return;

    setBusyCampaignId(campaign.id);
    setError('');
    setSendNotice('');

    try {
      await apiFetch(
        `/push-notifications/school/${schoolId}/campaigns/${campaign.id}/cancel`,
        { method: 'POST' },
        token
      );
      setSendNotice('Scheduled notification stopped.');
      await load();
    } catch (err) {
      setError(err.message || 'Unable to stop the scheduled notification.');
      if (err.status === 409) await load();
    } finally {
      setBusyCampaignId(null);
    }
  };

  const handleDelete = async (campaign) => {
    const confirmed = window.confirm(
      canCancelCampaign(campaign)
        ? `Delete the scheduled notification "${campaign.title}"? It will not be delivered and will be removed from history.`
        : `Delete "${campaign.title}" from the notification history? Delivery and open stats for it will be removed.`
    );
    if (!confirmed) return;

    setBusyCampaignId(campaign.id);
    setError('');
    setSendNotice('');

    try {
      await apiFetch(
        `/push-notifications/school/${schoolId}/campaigns/${campaign.id}`,
        { method: 'DELETE' },
        token
      );
      setSendNotice('Notification deleted.');
      await load();
    } catch (err) {
      setError(err.message || 'Unable to delete the notification.');
      if (err.status === 409) await load();
    } finally {
      setBusyCampaignId(null);
    }
  };

  return (
    <div className="spn-page">
      <section className="spn-header">
        <div>
          <h2>Push Notifications</h2>
          <p>Track reachable devices, delivery totals, opens, and send a manual alert to the school community.</p>
        </div>
        <button type="button" className="spn-refresh" onClick={load} disabled={loading}>
          Refresh
        </button>
      </section>

      {error ? <div className="spn-error">{error}</div> : null}
      {sendNotice ? <div className="spn-success">{sendNotice}</div> : null}

      <section className="spn-metrics" aria-label="Push notification totals">
        <div className="spn-metric">
          <span>Can send now</span>
          <strong>{formatNumber(audienceCounts.total)}</strong>
          <small>{formatNumber(audienceCounts.parents)} parents, {formatNumber(audienceCounts.staff)} staff</small>
        </div>
        <div className="spn-metric">
          <span>Notifications sent</span>
          <strong>{formatNumber(totals.sent_count)}</strong>
          <small>{formatNumber(totals.campaign_count)} campaigns</small>
        </div>
        <div className="spn-metric">
          <span>Scheduled</span>
          <strong>{formatNumber(totals.scheduled_count)}</strong>
          <small>Waiting to send</small>
        </div>
        <div className="spn-metric">
          <span>Opened</span>
          <strong>{formatNumber(totals.opened_count)}</strong>
          <small>{overallOpenRate} open rate</small>
        </div>
        <div className="spn-metric">
          <span>Failures</span>
          <strong>{formatNumber(totals.failed_count)}</strong>
          <small>{formatNumber(audienceCounts.total_registered)} registered devices</small>
        </div>
      </section>

      <div className="spn-grid">
        <section className="spn-panel">
          <div className="spn-panel-head">
            <h3>Create Notification</h3>
            <span>
              {targetCountLoading
                ? 'Counting reachable devices...'
                : targetCountError
                  ? 'Reachable count unavailable'
                  : `${formatNumber(selectedAudienceCount)} reachable`}
            </span>
          </div>

          <form className="spn-form" onSubmit={handleSend}>
            <label>
              Audience
              <select
                value={form.audience}
                onChange={(event) => handleChange('audience', event.target.value)}
                disabled={sending}
              >
                {AUDIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {form.audience === 'parents' ? (
              <fieldset className="spn-targeting" disabled={sending}>
                <legend>Target Parent Groups</legend>
                <div className="spn-targeting-head">
                  <p>Choose any grades and/or splits. Leave all unchecked to notify every parent.</p>
                  <div className="spn-targeting-actions">
                    <button
                      type="button"
                      onClick={handleSelectAllTargets}
                      disabled={allParentGroupsSelected}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearTargets}
                      disabled={!hasParentGroupTargets}
                    >
                      Clear selections
                    </button>
                  </div>
                </div>

                <div className="spn-target-section">
                  <h4>Grades</h4>
                  <div className="spn-target-grid">
                    {gradeOptions.map((grade) => (
                      <label key={grade.gradeCode} className="spn-target-option">
                        <input
                          type="checkbox"
                          checked={form.targetGrades.includes(grade.gradeCode)}
                          onChange={() => handleTargetToggle('targetGrades', grade.gradeCode)}
                        />
                        <span>{grade.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="spn-target-section">
                  <h4>Splits</h4>
                  {gradeSplitOptions.length ? (
                    <div className="spn-target-grid">
                      {gradeSplitOptions.map((split) => (
                        <label key={split.splitId} className="spn-target-option">
                          <input
                            type="checkbox"
                            checked={form.targetGradeSplitIds.includes(split.splitId)}
                            onChange={() => handleTargetToggle('targetGradeSplitIds', split.splitId)}
                          />
                          <span>{split.label}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="spn-target-empty">No active splits are configured for this school.</div>
                  )}
                </div>

                {targetCountError ? <div className="spn-target-count-error">{targetCountError}</div> : null}
              </fieldset>
            ) : null}

            <label>
              Title
              <input
                value={form.title}
                onChange={(event) => handleChange('title', event.target.value)}
                maxLength={120}
                placeholder="Dismissal update"
                disabled={sending}
              />
            </label>

            <label>
              Message
              <textarea
                value={form.body}
                onChange={(event) => handleChange('body', event.target.value)}
                maxLength={1000}
                placeholder="Write the notification body"
                disabled={sending}
              />
            </label>

            <div className="spn-scheduler">
              <div className="spn-scheduler-label">Schedule</div>
              <div className="spn-segmented" role="group" aria-label="Notification schedule">
                {SCHEDULE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={form.scheduleType === option.value ? 'active' : ''}
                    onClick={() => handleChange('scheduleType', option.value)}
                    disabled={sending}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {form.scheduleType === 'specific_time' ? (
                <div className="spn-schedule-grid">
                  <label>
                    Date
                    <input
                      type="date"
                      value={form.scheduleDate}
                      onChange={(event) => handleChange('scheduleDate', event.target.value)}
                      disabled={sending}
                    />
                  </label>
                  <label>
                    Time
                    <input
                      type="time"
                      value={form.scheduleTime}
                      onChange={(event) => handleChange('scheduleTime', event.target.value)}
                      disabled={sending}
                    />
                  </label>
                </div>
              ) : null}

              {form.scheduleType === 'dismissal_relative' ? (
                <div className="spn-schedule-grid">
                  <label>
                    Date
                    <input
                      type="date"
                      value={form.scheduleDate}
                      onChange={(event) => handleChange('scheduleDate', event.target.value)}
                      disabled={sending}
                    />
                  </label>
                  <label>
                    Offset
                    <input
                      type="number"
                      min="0"
                      max="720"
                      step="1"
                      value={form.relativeMinutes}
                      onChange={(event) => handleChange('relativeMinutes', event.target.value)}
                      disabled={sending}
                    />
                  </label>
                  <label>
                    Direction
                    <select
                      value={form.relativeDirection}
                      onChange={(event) => handleChange('relativeDirection', event.target.value)}
                      disabled={sending}
                    >
                      <option value="before">Before dismissal</option>
                      <option value="after">After dismissal</option>
                    </select>
                  </label>
                </div>
              ) : null}
            </div>

            <div className="spn-send-actions">
              <span>
                {form.scheduleType === 'immediate' ? 'Sends' : 'Schedules'} to devices with notifications enabled for {hasParentGroupTargets ? 'parents linked to any selected group' : getAudienceLabel(form.audience).toLowerCase()}.
              </span>
              <button type="submit" className="spn-send" disabled={!canSend}>
                {sending ? 'Saving...' : form.scheduleType === 'immediate' ? 'Send Notification' : 'Schedule Notification'}
              </button>
            </div>
          </form>
        </section>

        <section className="spn-panel">
          <div className="spn-panel-head">
            <h3>Audience Breakdown</h3>
            <span>Enabled devices</span>
          </div>
          <div className="spn-audience-list">
            <div>
              <strong>{formatNumber(audienceCounts.total)}</strong>
              <span>Everyone</span>
            </div>
            <div>
              <strong>{formatNumber(audienceCounts.parents)}</strong>
              <span>Parents</span>
            </div>
            <div>
              <strong>{formatNumber(audienceCounts.staff)}</strong>
              <span>Staff</span>
            </div>
          </div>
        </section>
      </div>

      <section className="spn-panel spn-history">
        <div className="spn-panel-head">
          <h3>Recent Notifications</h3>
          <span>{loading ? 'Loading...' : `${formatNumber(campaigns.length)} shown`}</span>
        </div>

        {campaigns.length ? (
          <div className="spn-table-wrap">
            <table className="spn-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Title</th>
                  <th>Audience</th>
                  <th>Target</th>
                  <th>Sent</th>
                  <th>Failed</th>
                  <th>Opened</th>
                  <th>Rate</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const targetSummary = formatPushTargetSummary(campaign, gradeOptions, gradeSplitOptions);
                  return (
                    <tr key={campaign.id}>
                      <td>{formatCampaignTime(campaign)}</td>
                      <td>
                        <strong>{campaign.title}</strong>
                        <span>{campaign.body}</span>
                        <span>{formatScheduleSummary(campaign)}</span>
                      </td>
                      <td>
                        {getAudienceLabel(campaign.audience)}
                        {targetSummary ? <span>{targetSummary}</span> : null}
                      </td>
                      <td>{formatNumber(campaign.target_count)}</td>
                      <td>{formatNumber(campaign.sent_count)}</td>
                      <td>{formatNumber(campaign.failed_count)}</td>
                      <td>{formatNumber(campaign.opened_count)}</td>
                      <td>{formatOpenRate(campaign.opened_count, campaign.sent_count)}</td>
                      <td>
                        <span className={`spn-status ${campaign.status}`}>
                          {getStatusLabel(campaign.status)}
                        </span>
                      </td>
                      <td>
                        <div className="spn-row-actions">
                          {canCancelCampaign(campaign) ? (
                            <button
                              type="button"
                              className="spn-row-action stop"
                              onClick={() => handleCancel(campaign)}
                              disabled={busyCampaignId === campaign.id}
                            >
                              Stop
                            </button>
                          ) : null}
                          {canDeleteCampaign(campaign) ? (
                            <button
                              type="button"
                              className="spn-row-action delete"
                              onClick={() => handleDelete(campaign)}
                              disabled={busyCampaignId === campaign.id}
                            >
                              Delete
                            </button>
                          ) : null}
                          {!canCancelCampaign(campaign) && !canDeleteCampaign(campaign) ? (
                            <span className="spn-row-action-empty">&mdash;</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="spn-empty">
            {loading ? 'Loading notification history...' : 'No push notifications have been sent yet.'}
          </div>
        )}
      </section>
    </div>
  );
}
